/**
 * Audit Logs Page
 *
 * Displays audit logs with filtering, expandable details, export, and live mode.
 * Capability-gated to users with admin:audit capability.
 */

import { useQuery } from "@tanstack/react-query";
import type { AuditEventCategory, AuditSeverity, Environment } from "@tatvex/types";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  Download,
  Radio,
  Play,
  Filter,
  Calendar,
  User,
  Target,
  Activity,
  Globe,
  Clock,
} from "lucide-react";
import type { ReactElement, ChangeEvent } from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Navigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type {
  AuditLogEntry,
  AuditLogsResponse,
  AuditFilters,
  ExportFormat,
  ExpandedRows,
} from "./types";
import {
  EVENT_CATEGORY_LABELS,
  EVENT_CATEGORY_COLORS,
  SEVERITY_INFO,
  ACTOR_TYPE_LABELS,
  EVENT_TYPES_BY_CATEGORY,
  TARGET_TYPES,
  DEFAULT_PAGE_SIZE,
  MAX_EXPORT_RECORDS,
  formatEventType,
  formatTimestamp,
  formatRelativeTime,
} from "./types";
import { useAuditStream } from "./useAuditStream";

export function AuditLogsPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:audit")) {
    return <Navigate to="/studio" replace />;
  }

  return <AuditLogsContent />;
}

function AuditLogsContent(): ReactElement {
  const [filters, setFilters] = useState<AuditFilters>({
    category: "all",
    eventType: "all",
    severity: "all",
    environment: "all",
    targetType: "all",
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Map Studio filter names to API query parameter names
      if (filters.eventType && filters.eventType !== "all")
        params.set("eventType", filters.eventType);
      if (filters.environment && filters.environment !== "all")
        params.set("environment", filters.environment);
      if (filters.targetType && filters.targetType !== "all")
        params.set("targetType", filters.targetType);
      if (filters.actorId) params.set("actorId", filters.actorId);
      if (filters.dateFrom) params.set("from", filters.dateFrom);
      if (filters.dateTo) params.set("to", filters.dateTo);
      // Map page/pageSize to limit/offset
      const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
      const page = filters.page ?? 1;
      params.set("limit", String(pageSize));
      params.set("offset", String((page - 1) * pageSize));

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await apiRequest<AuditLogsResponse>(`/api/v1/admin/audit${query}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !liveEnabled,
  });

  // Live mode stream
  const {
    liveState,
    start: startLive,
    stop: stopLive,
    pause: pauseLive,
    resume: resumeLive,
    pendingCount,
    clearPending,
  } = useAuditStream({
    enabled: liveEnabled,
    filters,
    onNewEntries: () => {
      refetch();
    },
  });

  // Handle scroll for auto-pause
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !liveEnabled) return;

    const handleScroll = (): void => {
      const currentScrollTop = container.scrollTop;
      const isScrollingUp = currentScrollTop < lastScrollTopRef.current;
      lastScrollTopRef.current = currentScrollTop;

      if (isScrollingUp && currentScrollTop > 0 && !liveState.paused) {
        pauseLive();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [liveEnabled, liveState.paused, pauseLive]);

  // Filter handlers
  const handleFilterChange = useCallback(
    <K extends keyof AuditFilters>(key: K, value: AuditFilters[K]): void => {
      setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    },
    []
  );

  const handleDateFromChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      handleFilterChange("dateFrom", e.target.value || undefined);
    },
    [handleFilterChange]
  );

  const handleDateToChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      handleFilterChange("dateTo", e.target.value || undefined);
    },
    [handleFilterChange]
  );

  const handleCategoryChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      handleFilterChange("category", e.target.value as AuditEventCategory | "all");
      // Reset event type when category changes
      handleFilterChange("eventType", "all");
    },
    [handleFilterChange]
  );

  const handleEventTypeChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      handleFilterChange("eventType", e.target.value as string);
    },
    [handleFilterChange]
  );

  const handleSeverityChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      handleFilterChange("severity", e.target.value as AuditSeverity | "all");
    },
    [handleFilterChange]
  );

  const handleEnvironmentChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      handleFilterChange("environment", e.target.value as Environment | "all");
    },
    [handleFilterChange]
  );

  const handleTargetTypeChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      handleFilterChange("targetType", e.target.value);
    },
    [handleFilterChange]
  );

  const handlePageChange = useCallback((newPage: number): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  // Row expansion
  const toggleRowExpanded = useCallback((id: string): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Live mode toggle
  const handleLiveToggle = useCallback((): void => {
    if (liveEnabled) {
      stopLive();
      setLiveEnabled(false);
    } else {
      startLive();
      setLiveEnabled(true);
    }
  }, [liveEnabled, startLive, stopLive]);

  // Export handlers
  const handleExport = useCallback(
    async (format: ExportFormat): Promise<void> => {
      const params = new URLSearchParams();
      params.set("format", format);
      params.set("limit", String(MAX_EXPORT_RECORDS));

      if (filters.category && filters.category !== "all") params.set("category", filters.category);
      if (filters.eventType && filters.eventType !== "all")
        params.set("eventType", filters.eventType);
      if (filters.severity && filters.severity !== "all") params.set("severity", filters.severity);
      if (filters.environment && filters.environment !== "all")
        params.set("environment", filters.environment);
      if (filters.dateFrom) params.set("from", filters.dateFrom);
      if (filters.dateTo) params.set("to", filters.dateTo);

      const query = params.toString() ? `?${params.toString()}` : "";

      // Open in new tab for download
      window.open(`/api/v1/admin/audit/export${query}`, "_blank");
    },
    [filters]
  );

  // Get event types for current category
  const availableEventTypes =
    filters.category && filters.category !== "all" ? EVENT_TYPES_BY_CATEGORY[filters.category] : [];

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Audit Logs</h1>
            <p className="mt-1 text-foreground-muted">View and export system audit events</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Live mode toggle */}
            <button
              onClick={handleLiveToggle}
              className={`btn ${liveEnabled ? "btn-primary" : "btn-secondary"}`}
            >
              {liveEnabled ? (
                <>
                  <Radio className="mr-2 h-4 w-4 animate-pulse" />
                  Live
                </>
              ) : (
                <>
                  <Radio className="mr-2 h-4 w-4" />
                  Live
                </>
              )}
            </button>

            {/* Export buttons */}
            <div className="relative group">
              <button className="btn btn-secondary">
                <Download className="mr-2 h-4 w-4" />
                Export
                <ChevronDown className="ml-2 h-4 w-4" />
              </button>
              <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-10">
                <div className="rounded-md border border-border bg-background shadow-lg py-1 min-w-32">
                  <button
                    onClick={() => handleExport("csv")}
                    className="block w-full px-4 py-2 text-left text-sm hover:bg-background-secondary"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport("json")}
                    className="block w-full px-4 py-2 text-left text-sm hover:bg-background-secondary"
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>

            {/* Toggle filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? "btn-primary" : "btn-secondary"}`}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </button>

            {/* Refresh */}
            <button onClick={() => refetch()} className="btn btn-secondary" disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Live mode indicator */}
        {liveEnabled && (
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  liveState.paused ? "bg-warning" : "bg-success animate-pulse"
                }`}
              />
              <span className="text-sm text-foreground-muted">
                {liveState.paused ? "Paused" : "Streaming live"}
              </span>
            </div>
            {liveState.paused && (
              <>
                <button
                  onClick={() => {
                    resumeLive();
                    clearPending();
                  }}
                  className="btn btn-secondary text-sm px-2 py-1"
                >
                  <Play className="mr-1 h-3 w-3" />
                  Resume
                </button>
                {pendingCount > 0 && (
                  <span className="text-sm text-primary">
                    {pendingCount} new {pendingCount === 1 ? "entry" : "entries"}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Filters panel */}
        {showFilters && (
          <div className="mt-4 grid gap-4 rounded-lg border border-border bg-background-secondary p-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Date range */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <Calendar className="inline-block h-3 w-3 mr-1" />
                From Date
              </label>
              <input
                type="datetime-local"
                value={filters.dateFrom ?? ""}
                onChange={handleDateFromChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                <Calendar className="inline-block h-3 w-3 mr-1" />
                To Date
              </label>
              <input
                type="datetime-local"
                value={filters.dateTo ?? ""}
                onChange={handleDateToChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Category filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <Activity className="inline-block h-3 w-3 mr-1" />
                Category
              </label>
              <select
                value={filters.category ?? "all"}
                onChange={handleCategoryChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Categories</option>
                {Object.entries(EVENT_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Event type filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <FileText className="inline-block h-3 w-3 mr-1" />
                Event Type
              </label>
              <select
                value={filters.eventType ?? "all"}
                onChange={handleEventTypeChange}
                disabled={!availableEventTypes.length}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              >
                <option value="all">All Events</option>
                {availableEventTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatEventType(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <AlertCircle className="inline-block h-3 w-3 mr-1" />
                Severity
              </label>
              <select
                value={filters.severity ?? "all"}
                onChange={handleSeverityChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Severities</option>
                {Object.entries(SEVERITY_INFO).map(([value, info]) => (
                  <option key={value} value={value}>
                    {info.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Environment filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <Globe className="inline-block h-3 w-3 mr-1" />
                Environment
              </label>
              <select
                value={filters.environment ?? "all"}
                onChange={handleEnvironmentChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Environments</option>
                <option value="dev">Development</option>
                <option value="staging">Staging</option>
                <option value="prod">Production</option>
              </select>
            </div>

            {/* Target type filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                <Target className="inline-block h-3 w-3 mr-1" />
                Target Type
              </label>
              <select
                value={filters.targetType ?? "all"}
                onChange={handleTargetTypeChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Types</option>
                {TARGET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Failed to load audit logs"}
            onRetry={refetch}
          />
        ) : data?.items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="p-6">
            {/* Audit log table */}
            <div className="rounded-lg border border-border bg-background-secondary overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-background">
                    <th className="w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Event
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Target
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Severity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Environment
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data?.items.map((entry) => (
                    <AuditLogRow
                      key={entry.id}
                      entry={entry}
                      isExpanded={expandedRows.has(entry.id)}
                      onToggle={() => toggleRowExpanded(entry.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.total > (filters.pageSize ?? DEFAULT_PAGE_SIZE) && (
              <Pagination
                page={filters.page ?? 1}
                pageSize={filters.pageSize ?? DEFAULT_PAGE_SIZE}
                total={data.total}
                hasMore={data.hasMore}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Audit Log Row
// ============================================================================

interface AuditLogRowProps {
  readonly entry: AuditLogEntry;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}

function AuditLogRow({ entry, isExpanded, onToggle }: AuditLogRowProps): ReactElement {
  const severityInfo = SEVERITY_INFO[entry.severity];
  const categoryColor = EVENT_CATEGORY_COLORS[entry.category];

  return (
    <>
      <tr onClick={onToggle} className="group hover:bg-background transition-colors cursor-pointer">
        <td className="px-2 py-3">
          <button className="p-1 text-foreground-muted">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-foreground-muted" />
            <div>
              <p className="text-sm font-mono">{formatRelativeTime(entry.timestamp)}</p>
              <p className="text-xs text-foreground-muted">{formatTimestamp(entry.timestamp)}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-foreground-muted" />
            <div>
              <p className="text-sm">{entry.actor.name ?? entry.actor.email ?? entry.actor.id}</p>
              <p className="text-xs text-foreground-muted">
                {ACTOR_TYPE_LABELS[entry.actor.type] ?? entry.actor.type}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div>
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-${categoryColor}-100 text-${categoryColor}-800`}
              style={{
                backgroundColor: `var(--color-${categoryColor}-100, #e0e7ff)`,
                color: `var(--color-${categoryColor}-800, #3730a3)`,
              }}
            >
              {EVENT_CATEGORY_LABELS[entry.category]}
            </span>
            <p className="mt-1 text-sm">{formatEventType(entry.eventType)}</p>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-foreground-muted" />
            <div>
              <p className="text-sm">{entry.target.name ?? entry.target.id}</p>
              <p className="text-xs text-foreground-muted">{entry.target.type}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              severityInfo.color === "error"
                ? "bg-error/10 text-error"
                : severityInfo.color === "warning"
                  ? "bg-warning/10 text-warning"
                  : "bg-foreground-muted/10 text-foreground-muted"
            }`}
          >
            {severityInfo.label}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-1 text-sm text-foreground-muted">
            <Globe className="h-3 w-3" />
            {entry.environment}
          </span>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-background-secondary px-4 py-4">
            <ExpandedDetails entry={entry} />
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Expanded Details
// ============================================================================

interface ExpandedDetailsProps {
  readonly entry: AuditLogEntry;
}

function ExpandedDetails({ entry }: ExpandedDetailsProps): ReactElement {
  return (
    <div className="space-y-4">
      {/* Source info */}
      <div className="rounded-lg border border-border bg-background p-4">
        <h4 className="text-sm font-semibold mb-2">Source Information</h4>
        <dl className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-foreground-muted">Request ID</dt>
            <dd className="font-mono text-xs">{entry.source.requestId}</dd>
          </div>
          {entry.source.ipAddress && (
            <div>
              <dt className="text-foreground-muted">IP Address</dt>
              <dd className="font-mono">{entry.source.ipAddress}</dd>
            </div>
          )}
          {entry.source.userAgent && (
            <div className="col-span-2">
              <dt className="text-foreground-muted">User Agent</dt>
              <dd className="text-xs truncate">{entry.source.userAgent}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Changes */}
      {entry.changes && (
        <div className="rounded-lg border border-border bg-background p-4">
          <h4 className="text-sm font-semibold mb-2">Changes</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            {entry.changes.before && (
              <div>
                <h5 className="text-xs font-medium text-foreground-muted mb-1">Before</h5>
                <pre className="rounded bg-background-secondary p-2 text-xs overflow-auto max-h-48">
                  {JSON.stringify(entry.changes.before, null, 2)}
                </pre>
              </div>
            )}
            {entry.changes.after && (
              <div>
                <h5 className="text-xs font-medium text-foreground-muted mb-1">After</h5>
                <pre className="rounded bg-background-secondary p-2 text-xs overflow-auto max-h-48">
                  {JSON.stringify(entry.changes.after, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      {entry.metadata && Object.keys(entry.metadata).length > 0 && (
        <div className="rounded-lg border border-border bg-background p-4">
          <h4 className="text-sm font-semibold mb-2">Metadata</h4>
          <pre className="rounded bg-background-secondary p-2 text-xs overflow-auto max-h-48">
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading audit logs...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <FileText className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">No audit logs found</h3>
      <p className="mt-2 text-foreground-muted">Try adjusting your filters or check back later</p>
    </div>
  );
}

// ============================================================================
// Pagination Component
// ============================================================================

interface PaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onPageChange: (page: number) => void;
}

function Pagination({
  page,
  pageSize,
  total,
  hasMore,
  onPageChange,
}: PaginationProps): ReactElement {
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-foreground-muted">
        Showing {start} to {end} of {total} entries
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-foreground-muted">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasMore}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
